import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Zap, Globe, Lock, BarChart3, Users, CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import AnimatedSection from '@/components/AnimatedSection';
import SEOHead from '@/components/SEOHead';
import { useLanguagePrefix } from '@/hooks/useLanguagePrefix';

const HomePage = () => {
  const { t } = useTranslation();
  const langPrefix = useLanguagePrefix();
  const features = [
    {
      icon: <Shield className="h-8 w-8 text-kitco-gold" />,
      title: 'Government Vault Storage',
      description: 'Audited gold reserves with monthly third-party attestations ensuring full transparency and security.'
    },
    {
      icon: <Zap className="h-8 w-8 text-kitco-gold" />,
      title: 'Blockchain Gold Trading',
      description: 'Fast, low-cost transactions with on-chain metal ownership and transparent settlement.'
    },
    {
      icon: <Globe className="h-8 w-8 text-kitco-gold" />,
      title: 'Physically-Backed Gold',
      description: 'Every token represents 1:1 gold tokenization with LBMA compliant redeemable gold.'
    },
    {
      icon: <Lock className="h-8 w-8 text-kitco-gold" />,
      title: 'Fireblocks Custody',
      description: 'Institutional-grade security with ERC-3643 compliance and full insurance coverage.'
    },
    {
      icon: <BarChart3 className="h-8 w-8 text-kitco-gold" />,
      title: 'Seamless API Integration',
      description: 'Integrate with your existing CRM, accounting and reporting systems effortlessly.'
    },
    {
      icon: <Users className="h-8 w-8 text-kitco-gold" />,
      title: 'Professional Support',
      description: 'Access to professional trade desk, marketing and sales support for your business.'
    }
  ];

  // Use translated institutions data
  const institutions = [
    { title: t('home.institutions.types.buyers.title'), description: t('home.institutions.types.buyers.description') },
    { title: t('home.institutions.types.custodians.title'), description: t('home.institutions.types.custodians.description') },
    { title: t('home.institutions.types.brokerage.title'), description: t('home.institutions.types.brokerage.description') },
    { title: t('home.institutions.types.hedgeFunds.title'), description: t('home.institutions.types.hedgeFunds.description') },
    { title: t('home.institutions.types.trust.title'), description: t('home.institutions.types.trust.description') },
    { title: t('home.institutions.types.banks.title'), description: t('home.institutions.types.banks.description') },
    { title: t('home.institutions.types.family.title'), description: t('home.institutions.types.family.description') },
    { title: t('home.institutions.types.advisors.title'), description: t('home.institutions.types.advisors.description') }
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      <SEOHead 
        title="Kitco DirectReserve | Tokenized Precious Metals Platform for Institutions"
        description="Trade and hold tokenized, physically backed gold and silver with Kitco DirectReserve. Real world asset (RWA) tokenization with audited 1:1 reserves, government vault storage, institutional APIs."
        keywords="digital gold, tokenized precious metals, physically-backed gold, government vault storage, audited gold reserves, Kitco DirectReserve, KDRGOLD, KDRSILVER, 1:1 gold tokenization, LBMA compliant, RWA, real world assets, asset tokenization"
        url="https://kitcodigitalmetals.com"
      />
      
      {/* Organization Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Kitco DirectReserve",
          "alternateName": "Kitco Digital Metals",
          "url": "https://kitcodigitalmetals.com",
          "logo": "https://customer-assets.emergentagent.com/job_kitcorebuild/artifacts/q2yxepff_Digital_White.png",
          "sameAs": ["https://www.kitco.com/"],
          "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+1-833-972-2424",
            "contactType": "customer service",
            "areaServed": ["US", "CA", "Worldwide"],
            "availableLanguage": "English"
          },
          "address": {
            "@type": "PostalAddress",
            "addressCountry": "CA",
            "addressLocality": "Montreal"
          }
        })}
      </script>
      
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center text-white py-40 md:py-48"
        style={{
          backgroundImage: 'linear-gradient(rgba(41, 51, 67, 0.88), rgba(10, 10, 10, 0.88)), url(https://images.unsplash.com/photo-1610375461246-83df859d849d)',
          backgroundAttachment: 'fixed'
        }}
        data-testid="hero-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-7xl font-bold mb-8 font-lato leading-tight" data-testid="hero-title">
            {t('home.hero.title')}
          </h1>
          <p className="text-xl md:text-2xl mb-12 text-white/90 max-w-4xl mx-auto leading-relaxed font-light" data-testid="hero-subtitle">
            {t('home.hero.subtitle')}
          </p>
          <Link to={`${langPrefix}/contact`}>
            <Button size="lg" className="bg-kitco-gold hover:bg-kitco-gold-hover text-kitco-navy font-bold text-lg px-12 py-7 transition-all shadow-2xl shadow-kitco-gold/40 hover:shadow-kitco-gold/60 hover:scale-105 rounded-lg" data-testid="hero-cta-button">
              {t('nav.contact')} <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 bg-gradient-to-b from-grey-50 to-white" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fade-in-up">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-kitco-navy mb-6 font-lato" data-testid="about-title">
                {t('home.about.title')}
              </h2>
              <div className="w-32 h-1.5 bg-gradient-to-r from-kitco-gold via-kitco-gold-hover to-kitco-gold mx-auto mb-6 rounded-full"></div>
            </div>
          </AnimatedSection>
          <AnimatedSection animation="fade-in-up" delay={200}>
            <div className="max-w-4xl mx-auto text-xl text-grey-600 leading-relaxed" data-testid="about-content">
              <p className="mb-6">
                {t('home.about.description')}
              </p>
              <p>
                {t('home.about.mission')}
              </p>
            </div>
          </AnimatedSection>

          {/* Partners Section - Hidden for now */}
          {/* <AnimatedSection animation="fade-in-up" delay={400}>
            <div className="mt-16">
              <h3 className="text-center text-2xl font-semibold text-kitco-navy mb-8" data-testid="partners-title">Trusted Partners & Affiliates</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 items-center justify-items-center opacity-60 grayscale hover:opacity-100 hover:grayscale-0 transition-all" data-testid="partners-grid">
              <div className="text-center">
                <p className="font-semibold text-grey-600">Royal Canadian Mint</p>
              </div>
              <div className="text-center">
                <p className="font-semibold text-grey-600">Perth Mint</p>
              </div>
              <div className="text-center">
                <p className="font-semibold text-grey-600">PAMP</p>
              </div>
              <div className="text-center">
                <p className="font-semibold text-grey-600">Johnson Matthey</p>
              </div>
              <div className="text-center">
                <p className="font-semibold text-grey-600">HSBC</p>
              </div>
              <div className="text-center">
                <p className="font-semibold text-grey-600">Brinks</p>
              </div>
              <div className="text-center">
                <p className="font-semibold text-grey-600">Loomis</p>
              </div>
              <div className="text-center">
                <p className="font-semibold text-grey-600">Degussa</p>
              </div>
              <div className="text-center">
                <p className="font-semibold text-grey-600">Sumitomo</p>
              </div>
              <div className="text-center">
                <p className="font-semibold text-grey-600">ICBC Asia</p>
              </div>
              <div className="text-center">
                <p className="font-semibold text-grey-600">Questrade</p>
              </div>
            </div>
            </div>
          </AnimatedSection> */}
        </div>
      </section>

      {/* Institutions Section */}
      <section className="py-24 bg-gradient-to-br from-kitco-navy via-grey-800 to-kitco-navy text-white relative overflow-hidden" data-testid="institutions-section">
        {/* Subtle decorative elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-kitco-gold/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-kitco-teal/5 rounded-full blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <AnimatedSection animation="fade-in-up">
            <div className="text-center mb-20">
              <h2 className="text-5xl font-bold mb-6" data-testid="institutions-title">
                {t('home.institutions.title')}
              </h2>
              <div className="w-32 h-1.5 bg-gradient-to-r from-kitco-gold via-kitco-gold-hover to-kitco-gold mx-auto mb-8 rounded-full"></div>
              <p className="text-2xl text-white/80 font-light max-w-3xl mx-auto" data-testid="institutions-subtitle">
                {t('home.institutions.subtitle')}
              </p>
            </div>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {institutions.map((institution, index) => (
              <AnimatedSection key={index} animation="fade-in-up" delay={index * 100}>
                <div className="bg-white/5 backdrop-blur-sm p-8 rounded-xl border border-kitco-gold/20 hover:border-kitco-gold hover:bg-white/10 transition-all hover:scale-105 hover:shadow-xl hover:shadow-kitco-gold/10 h-full group" data-testid={`institution-card-${index}`}>
                  <h3 className="text-xl font-bold mb-4 text-white group-hover:text-kitco-gold transition-colors" data-testid={`institution-title-${index}`}>{institution.title}</h3>
                  <p className="text-white/70 leading-relaxed" data-testid={`institution-description-${index}`}>{institution.description}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Partner Benefits Section */}
      <section className="py-20 bg-grey-50" data-testid="partner-benefits-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fade-in-up">
            <div className="max-w-4xl mx-auto mb-12">
              <p className="text-lg text-grey-600 leading-relaxed mb-6">
                {t('home.partnerBenefits.intro')}
              </p>
            </div>
          </AnimatedSection>

          <AnimatedSection animation="fade-in-up" delay={200}>
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-kitco-navy mb-8 text-center">{t('home.partnerBenefits.apiTitle')}</h2>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            <AnimatedSection animation="scale-in" delay={100}>
              <Card className="relative overflow-hidden bg-gradient-to-br from-grey-50 to-kitco-gold-light hover:shadow-2xl hover:scale-105 transition-all duration-300 h-full border border-kitco-gold/20 group">
                <div className="absolute top-0 right-0 w-32 h-32 bg-kitco-gold/10 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500"></div>
                <CardContent className="p-6 relative z-10">
                  <div className="bg-gradient-to-br from-kitco-gold to-kitco-gold-hover p-3 rounded-lg inline-block mb-4 group-hover:rotate-12 transition-transform">
                    <CheckCircle2 className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold mb-3 text-kitco-navy">{t('home.partnerBenefits.cards.operational.title')}</h3>
                  <p className="text-grey-500 text-sm leading-relaxed">{t('home.partnerBenefits.cards.operational.description')}</p>
                </CardContent>
              </Card>
            </AnimatedSection>

            <AnimatedSection animation="scale-in" delay={200}>
              <Card className="relative overflow-hidden bg-gradient-to-br from-grey-50 to-kitco-gold-light hover:shadow-2xl hover:scale-105 transition-all duration-300 h-full border border-kitco-gold/20 group">
                <div className="absolute top-0 right-0 w-32 h-32 bg-kitco-gold/10 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500"></div>
                <CardContent className="p-6 relative z-10">
                  <div className="bg-gradient-to-br from-kitco-gold to-kitco-gold-hover p-3 rounded-lg inline-block mb-4 group-hover:rotate-12 transition-transform">
                    <CheckCircle2 className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold mb-3 text-kitco-navy">{t('home.partnerBenefits.cards.endToEnd.title')}</h3>
                  <p className="text-grey-500 text-sm leading-relaxed">{t('home.partnerBenefits.cards.endToEnd.description')}</p>
                </CardContent>
              </Card>
            </AnimatedSection>

            <AnimatedSection animation="scale-in" delay={300}>
              <Card className="relative overflow-hidden bg-gradient-to-br from-grey-50 to-kitco-gold-light hover:shadow-2xl hover:scale-105 transition-all duration-300 h-full border border-kitco-gold/20 group">
                <div className="absolute top-0 right-0 w-32 h-32 bg-kitco-gold/10 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500"></div>
                <CardContent className="p-6 relative z-10">
                  <div className="bg-gradient-to-br from-kitco-gold to-kitco-gold-hover p-3 rounded-lg inline-block mb-4 group-hover:rotate-12 transition-transform">
                    <CheckCircle2 className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold mb-3 text-kitco-navy">{t('home.partnerBenefits.cards.efficiency.title')}</h3>
                  <p className="text-grey-500 text-sm leading-relaxed">{t('home.partnerBenefits.cards.efficiency.description')}</p>
                </CardContent>
              </Card>
            </AnimatedSection>

            <AnimatedSection animation="scale-in" delay={400}>
              <Card className="relative overflow-hidden bg-gradient-to-br from-grey-50 to-kitco-gold-light hover:shadow-2xl hover:scale-105 transition-all duration-300 h-full border border-kitco-gold/20 group">
                <div className="absolute top-0 right-0 w-32 h-32 bg-kitco-gold/10 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500"></div>
                <CardContent className="p-6 relative z-10">
                  <div className="bg-gradient-to-br from-kitco-gold to-kitco-gold-hover p-3 rounded-lg inline-block mb-4 group-hover:rotate-12 transition-transform">
                    <CheckCircle2 className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold mb-3 text-kitco-navy">{t('home.partnerBenefits.cards.tradingDesk.title')}</h3>
                  <p className="text-grey-500 text-sm leading-relaxed">{t('home.partnerBenefits.cards.tradingDesk.description')}</p>
                </CardContent>
              </Card>
            </AnimatedSection>

            <AnimatedSection animation="scale-in" delay={500}>
              <Card className="relative overflow-hidden bg-gradient-to-br from-grey-50 to-kitco-gold-light hover:shadow-2xl hover:scale-105 transition-all duration-300 h-full border border-kitco-gold/20 group">
                <div className="absolute top-0 right-0 w-32 h-32 bg-kitco-gold/10 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500"></div>
                <CardContent className="p-6 relative z-10">
                  <div className="bg-gradient-to-br from-kitco-gold to-kitco-gold-hover p-3 rounded-lg inline-block mb-4 group-hover:rotate-12 transition-transform">
                    <CheckCircle2 className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold mb-3 text-kitco-navy">{t('home.partnerBenefits.cards.physicalConversion.title')}</h3>
                  <p className="text-grey-500 text-sm leading-relaxed">{t('home.partnerBenefits.cards.physicalConversion.description')}</p>
                </CardContent>
              </Card>
            </AnimatedSection>

            <AnimatedSection animation="scale-in" delay={600}>
              <Card className="relative overflow-hidden bg-gradient-to-br from-grey-50 to-kitco-gold-light hover:shadow-2xl hover:scale-105 transition-all duration-300 h-full border border-kitco-gold/20 group">
                <div className="absolute top-0 right-0 w-32 h-32 bg-kitco-gold/10 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500"></div>
                <CardContent className="p-6 relative z-10">
                  <div className="bg-gradient-to-br from-kitco-gold to-kitco-gold-hover p-3 rounded-lg inline-block mb-4 group-hover:rotate-12 transition-transform">
                    <CheckCircle2 className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold mb-3 text-kitco-navy">{t('home.partnerBenefits.cards.distributionServices.title')}</h3>
                  <p className="text-grey-500 text-sm leading-relaxed">{t('home.partnerBenefits.cards.distributionServices.description')}</p>
                </CardContent>
              </Card>
            </AnimatedSection>

            <AnimatedSection animation="scale-in" delay={700}>
              <Card className="relative overflow-hidden bg-gradient-to-br from-grey-50 to-kitco-gold-light hover:shadow-2xl hover:scale-105 transition-all duration-300 h-full border border-kitco-gold/20 group">
                <div className="absolute top-0 right-0 w-32 h-32 bg-kitco-gold/10 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500"></div>
                <CardContent className="p-6 relative z-10">
                  <div className="bg-gradient-to-br from-kitco-gold to-kitco-gold-hover p-3 rounded-lg inline-block mb-4 group-hover:rotate-12 transition-transform">
                    <CheckCircle2 className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold mb-3 text-kitco-navy">{t('home.partnerBenefits.cards.apiIntegration.title')}</h3>
                  <p className="text-grey-500 text-sm leading-relaxed">{t('home.partnerBenefits.cards.apiIntegration.description')}</p>
                </CardContent>
              </Card>
            </AnimatedSection>

            <AnimatedSection animation="scale-in" delay={800}>
              <Card className="relative overflow-hidden bg-gradient-to-br from-grey-50 to-kitco-gold-light hover:shadow-2xl hover:scale-105 transition-all duration-300 h-full border border-kitco-gold/20 group">
                <div className="absolute top-0 right-0 w-32 h-32 bg-kitco-gold/10 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500"></div>
                <CardContent className="p-6 relative z-10">
                  <div className="bg-gradient-to-br from-kitco-gold to-kitco-gold-hover p-3 rounded-lg inline-block mb-4 group-hover:rotate-12 transition-transform">
                    <CheckCircle2 className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold mb-3 text-kitco-navy">{t('home.partnerBenefits.cards.businessGrowth.title')}</h3>
                  <p className="text-grey-500 text-sm leading-relaxed">{t('home.partnerBenefits.cards.businessGrowth.description')}</p>
                </CardContent>
              </Card>
            </AnimatedSection>

            <AnimatedSection animation="scale-in" delay={900}>
              <Card className="relative overflow-hidden bg-gradient-to-br from-grey-50 to-kitco-gold-light hover:shadow-2xl hover:scale-105 transition-all duration-300 h-full border border-kitco-gold/20 group">
                <div className="absolute top-0 right-0 w-32 h-32 bg-kitco-gold/10 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500"></div>
                <CardContent className="p-6 relative z-10">
                  <div className="bg-gradient-to-br from-kitco-gold to-kitco-gold-hover p-3 rounded-lg inline-block mb-4 group-hover:rotate-12 transition-transform">
                    <CheckCircle2 className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold mb-3 text-kitco-navy">{t('home.partnerBenefits.cards.marketingSupport.title')}</h3>
                  <p className="text-grey-500 text-sm leading-relaxed">{t('home.partnerBenefits.cards.marketingSupport.description')}</p>
                </CardContent>
              </Card>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* DirectReserve Showcase - Premium Feature Section */}
      <section className="py-20 bg-kitco-navy relative overflow-hidden" data-testid="directreserve-showcase">
        {/* Animated gradient background */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-gradient-to-br from-kitco-teal via-kitco-teal-hover to-kitco-teal animate-gradient"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <p className="text-kitco-gold font-semibold text-sm uppercase tracking-wider mb-3">{t('home.showcase.subtitle')}</p>
            <h2 className="text-5xl md:text-6xl font-bold text-white mb-4">
              {t('home.showcase.title')}
            </h2>
            <p className="text-xl text-grey-300 max-w-3xl mx-auto">
              {t('home.showcase.futureText')}
            </p>
          </div>

          {/* Main Feature Card */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            <Link to={`${langPrefix}/direct-reserve`} className="group">
              <div className="relative bg-gradient-to-br from-kitco-navy to-grey-900 rounded-2xl p-8 border-2 border-kitco-gold/30 hover:border-kitco-gold transition-all duration-500 overflow-hidden h-full hover:scale-105 hover:shadow-2xl hover:shadow-kitco-gold/30">
                {/* Glow effect on hover */}
                <div className="absolute inset-0 bg-gradient-to-r from-kitco-gold/0 via-kitco-gold/10 to-kitco-gold/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-3xl font-bold text-white group-hover:text-kitco-gold transition-colors">
                      {t('home.showcase.mainCard.title')}
                    </h3>
                    <ArrowRight className="h-8 w-8 text-kitco-gold group-hover:translate-x-2 transition-transform" />
                  </div>
                  
                  <p className="text-grey-300 text-lg mb-6 leading-relaxed">
                    {t('home.showcase.mainCard.description')}
                  </p>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-kitco-gold/10 p-4 rounded-lg border border-kitco-gold/30">
                      <div className="text-kitco-gold font-bold text-2xl mb-1">24/7</div>
                      <div className="text-grey-300 text-sm">{t('home.showcase.mainCard.trading')}</div>
                    </div>
                    <div className="bg-kitco-gold/10 p-4 rounded-lg border border-kitco-gold/30">
                      <div className="text-kitco-gold font-bold text-2xl mb-1">1:1</div>
                      <div className="text-grey-300 text-sm">{t('home.showcase.mainCard.backed')}</div>
                    </div>
                  </div>
                </div>
              </div>
            </Link>

            {/* Feature Grid */}
            <div className="grid grid-cols-2 gap-4">
              <Link to={`${langPrefix}/direct-reserve`}>
                <div className="group bg-kitco-navy p-6 rounded-xl border border-kitco-gold/30 hover:border-kitco-gold transition-all duration-300 hover:scale-105 cursor-pointer h-full">
                  <Shield className="h-10 w-10 text-kitco-gold mb-4 group-hover:scale-110 transition-transform" />
                  <h4 className="text-white font-bold text-lg mb-2">{t('home.showcase.features.erc3643.title')}</h4>
                  <p className="text-grey-300 text-sm">{t('home.showcase.features.erc3643.description')}</p>
                </div>
              </Link>

              <Link to={`${langPrefix}/direct-reserve`}>
                <div className="group bg-kitco-navy p-6 rounded-xl border border-kitco-gold/30 hover:border-kitco-gold transition-all duration-300 hover:scale-105 cursor-pointer h-full">
                  <Lock className="h-10 w-10 text-kitco-gold mb-4 group-hover:scale-110 transition-transform" />
                  <h4 className="text-white font-bold text-lg mb-2">{t('home.showcase.features.fireblocks.title')}</h4>
                  <p className="text-grey-300 text-sm">{t('home.showcase.features.fireblocks.description')}</p>
                </div>
              </Link>

              <Link to={`${langPrefix}/direct-reserve`}>
                <div className="group bg-kitco-navy p-6 rounded-xl border border-kitco-gold/30 hover:border-kitco-gold transition-all duration-300 hover:scale-105 cursor-pointer h-full">
                  <Zap className="h-10 w-10 text-kitco-gold mb-4 group-hover:scale-110 transition-transform" />
                  <h4 className="text-white font-bold text-lg mb-2">{t('home.showcase.features.settlement.title')}</h4>
                  <p className="text-grey-300 text-sm">{t('home.showcase.features.settlement.description')}</p>
                </div>
              </Link>

              <Link to={`${langPrefix}/direct-reserve`}>
                <div className="group bg-kitco-navy p-6 rounded-xl border border-kitco-gold/30 hover:border-kitco-gold transition-all duration-300 hover:scale-105 cursor-pointer h-full">
                  <BarChart3 className="h-10 w-10 text-kitco-gold mb-4 group-hover:scale-110 transition-transform" />
                  <h4 className="text-white font-bold text-lg mb-2">{t('home.showcase.features.api.title')}</h4>
                  <p className="text-grey-300 text-sm">{t('home.showcase.features.api.description')}</p>
                </div>
              </Link>
            </div>
          </div>

          {/* CTA Banner */}
          <div className="text-center">
            <Link to={`${langPrefix}/direct-reserve`} onClick={() => window.scrollTo(0, 0)}>
              <Button size="lg" className="bg-kitco-gold hover:bg-kitco-gold-hover text-kitco-navy font-bold text-lg px-12 py-6 shadow-lg shadow-kitco-gold/50 hover:shadow-kitco-gold/70 transition-all hover:scale-105">
                {t('home.showcase.exploreButton')} <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Kitco Institutional Section */}
      <section className="py-24 bg-gradient-to-br from-kitco-navy via-grey-900 to-grey-900 relative overflow-hidden" data-testid="why-kitco-section">
        {/* Decorative background elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-64 h-64 bg-kitco-gold rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-64 h-64 bg-kitco-teal rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-bold text-white mb-6">{t('home.why.title')}</h2>
            <p className="text-2xl text-kitco-gold font-semibold">{t('home.why.subtitle')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {/* Trusted Heritage */}
            <div className="group bg-white/5 backdrop-blur-sm p-8 rounded-2xl border-2 border-kitco-gold/20 hover:border-kitco-gold transition-all hover:scale-105 hover:shadow-2xl hover:shadow-kitco-gold/20">
              <div className="mb-6">
                <div className="w-16 h-16 bg-kitco-gold/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-kitco-gold/20 transition-colors">
                  <BarChart3 className="h-10 w-10 text-kitco-gold" />
                </div>
                <h3 className="text-2xl font-bold text-white group-hover:text-kitco-gold transition-colors">{t('home.why.trust.title')}</h3>
              </div>
              <p className="text-white/70 leading-relaxed">
                {t('home.why.trust.description')}
              </p>
            </div>

            {/* RWA Pioneer */}
            <div className="group bg-white/5 backdrop-blur-sm p-8 rounded-2xl border-2 border-kitco-gold/20 hover:border-kitco-gold transition-all hover:scale-105 hover:shadow-2xl hover:shadow-kitco-gold/20">
              <div className="mb-6">
                <div className="w-16 h-16 bg-kitco-gold/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-kitco-gold/20 transition-colors">
                  <Globe className="h-10 w-10 text-kitco-gold" />
                </div>
                <h3 className="text-2xl font-bold text-white group-hover:text-kitco-gold transition-colors">{t('home.why.rwa.title')}</h3>
              </div>
              <p className="text-white/70 leading-relaxed">
                {t('home.why.rwa.description')}
              </p>
            </div>

            {/* Innovation Meets Tradition */}
            <div className="group bg-white/5 backdrop-blur-sm p-8 rounded-2xl border-2 border-kitco-gold/20 hover:border-kitco-gold transition-all hover:scale-105 hover:shadow-2xl hover:shadow-kitco-gold/20">
              <div className="mb-6">
                <div className="w-16 h-16 bg-kitco-gold/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-kitco-gold/20 transition-colors">
                  <Shield className="h-10 w-10 text-kitco-gold" />
                </div>
                <h3 className="text-2xl font-bold text-white group-hover:text-kitco-gold transition-colors">{t('home.why.innovation.title')}</h3>
              </div>
              <p className="text-white/70 leading-relaxed">
                {t('home.why.innovation.description')}
              </p>
            </div>

            {/* White-Glove Service */}
            <div className="group bg-white/5 backdrop-blur-sm p-8 rounded-2xl border-2 border-kitco-gold/20 hover:border-kitco-gold transition-all hover:scale-105 hover:shadow-2xl hover:shadow-kitco-gold/20">
              <div className="mb-6">
                <div className="w-16 h-16 bg-kitco-gold/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-kitco-gold/20 transition-colors">
                  <Users className="h-10 w-10 text-kitco-gold" />
                </div>
                <h3 className="text-2xl font-bold text-white group-hover:text-kitco-gold transition-colors">{t('home.why.service.title')}</h3>
              </div>
              <p className="text-white/70 leading-relaxed">
                {t('home.why.service.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-white" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fade-in-up">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-kitco-navy mb-4">{t('home.howItWorks.title')}</h2>
              <div className="w-32 h-1.5 bg-gradient-to-r from-kitco-gold via-kitco-gold-hover to-kitco-gold mx-auto mb-6 rounded-full"></div>
              <p className="text-2xl text-grey-600 font-light">{t('home.howItWorks.subtitle')}</p>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 relative">
            {/* Connection lines for desktop */}
            <div className="hidden lg:block absolute top-20 left-0 right-0 h-0.5 bg-gradient-to-r from-kitco-gold/0 via-kitco-gold to-kitco-gold/0"></div>

            {/* Step 1 */}
            <AnimatedSection animation="fade-in-up" delay={100}>
              <div className="relative bg-gradient-to-br from-grey-50 to-white p-8 rounded-2xl border-2 border-kitco-gold/20 hover:border-kitco-gold hover:shadow-xl transition-all text-center group">
                <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 w-12 h-12 bg-kitco-gold rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg group-hover:scale-110 transition-transform">
                  1
                </div>
                <h3 className="text-xl font-bold text-kitco-navy mb-4 mt-4">{t('home.howItWorks.step1.title')}</h3>
                <p className="text-grey-600 leading-relaxed">{t('home.howItWorks.step1.description')}</p>
              </div>
            </AnimatedSection>

            {/* Step 2 */}
            <AnimatedSection animation="fade-in-up" delay={200}>
              <div className="relative bg-gradient-to-br from-grey-50 to-white p-8 rounded-2xl border-2 border-kitco-gold/20 hover:border-kitco-gold hover:shadow-xl transition-all text-center group">
                <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 w-12 h-12 bg-kitco-gold rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg group-hover:scale-110 transition-transform">
                  2
                </div>
                <h3 className="text-xl font-bold text-kitco-navy mb-4 mt-4">{t('home.howItWorks.step2.title')}</h3>
                <p className="text-grey-600 leading-relaxed">{t('home.howItWorks.step2.description')}</p>
              </div>
            </AnimatedSection>

            {/* Step 3 */}
            <AnimatedSection animation="fade-in-up" delay={300}>
              <div className="relative bg-gradient-to-br from-grey-50 to-white p-8 rounded-2xl border-2 border-kitco-gold/20 hover:border-kitco-gold hover:shadow-xl transition-all text-center group">
                <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 w-12 h-12 bg-kitco-gold rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg group-hover:scale-110 transition-transform">
                  3
                </div>
                <h3 className="text-xl font-bold text-kitco-navy mb-4 mt-4">{t('home.howItWorks.step3.title')}</h3>
                <p className="text-grey-600 leading-relaxed">{t('home.howItWorks.step3.description')}</p>
              </div>
            </AnimatedSection>

            {/* Step 4 */}
            <AnimatedSection animation="fade-in-up" delay={400}>
              <div className="relative bg-gradient-to-br from-grey-50 to-white p-8 rounded-2xl border-2 border-kitco-gold/20 hover:border-kitco-gold hover:shadow-xl transition-all text-center group">
                <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 w-12 h-12 bg-kitco-gold rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg group-hover:scale-110 transition-transform">
                  4
                </div>
                <h3 className="text-xl font-bold text-kitco-navy mb-4 mt-4">{t('home.howItWorks.step4.title')}</h3>
                <p className="text-grey-600 leading-relaxed">{t('home.howItWorks.step4.description')}</p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Testimonial Section - Hidden until verified */}
      {/* <section className="py-20 bg-gradient-to-br from-kitco-navy to-grey-800 text-white relative overflow-hidden" data-testid="testimonial-section">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 right-0 w-96 h-96 bg-kitco-gold rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <AnimatedSection animation="fade-in-up">
            <div className="text-center">
              <div className="mb-8">
                <svg className="w-16 h-16 text-kitco-gold mx-auto" fill="currentColor" viewBox="0 0 32 32">
                  <path d="M10 8c-3.3 0-6 2.7-6 6v10h10V14h-6c0-2.2 1.8-4 4-4V8zm16 0c-3.3 0-6 2.7-6 6v10h10V14h-6c0-2.2 1.8-4 4-4V8z"/>
                </svg>
              </div>
              <blockquote className="text-2xl md:text-3xl font-light leading-relaxed mb-8 text-white/90">
                "{t('home.testimonial.quote')}"
              </blockquote>
              <div className="border-t border-kitco-gold/30 pt-6 mt-6">
                <p className="text-kitco-gold font-semibold text-lg">{t('home.testimonial.author')}</p>
                <p className="text-white/70">{t('home.testimonial.company')}</p>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section> */}

      {/* FAQ Section */}
      <section className="py-20 bg-grey-50" data-testid="faq-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-kitco-navy mb-12 text-center">{t('home.faq.title')}</h2>
          
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="item-1">
              <AccordionTrigger className="text-left text-lg font-semibold">
                {t('home.faq.q1.question')}
              </AccordionTrigger>
              <AccordionContent className="text-grey-500">
                <div dangerouslySetInnerHTML={{ __html: t('home.faq.q1.answer') }} />
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2">
              <AccordionTrigger className="text-left text-lg font-semibold">
                {t('home.faq.q2.question')}
              </AccordionTrigger>
              <AccordionContent className="text-grey-500">
                {t('home.faq.q2.answer')}
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3">
              <AccordionTrigger className="text-left text-lg font-semibold">
                {t('home.faq.q3.question')}
              </AccordionTrigger>
              <AccordionContent className="text-grey-500">
                <div dangerouslySetInnerHTML={{ __html: t('home.faq.q3.answer') }} />
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4">
              <AccordionTrigger className="text-left text-lg font-semibold">
                {t('home.faq.q4.question')}
              </AccordionTrigger>
              <AccordionContent className="text-grey-500">
                <div dangerouslySetInnerHTML={{ __html: t('home.faq.q4.answer') }} />
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5">
              <AccordionTrigger className="text-left text-lg font-semibold">
                {t('home.faq.q5.question')}
              </AccordionTrigger>
              <AccordionContent className="text-grey-500">
                <div dangerouslySetInnerHTML={{ __html: t('home.faq.q5.answer') }} />
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-6">
              <AccordionTrigger className="text-left text-lg font-semibold">
                {t('home.faq.q6.question')}
              </AccordionTrigger>
              <AccordionContent className="text-grey-500">
                <div dangerouslySetInnerHTML={{ __html: t('home.faq.q6.answer') }} />
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-7">
              <AccordionTrigger className="text-left text-lg font-semibold">
                {t('home.faq.q7.question')}
              </AccordionTrigger>
              <AccordionContent className="text-grey-500">
                {t('home.faq.q7.answer')}
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>
      
      {/* FAQ Schema */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "Is DirectReserve fully backed 1:1?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes—every KDRGOLD and KDRSILVER token corresponds to an ounce of metal held at Kitco DR Vault using government depositories, with independent third-party attestations confirming exact balances monthly."
              }
            },
            {
              "@type": "Question",
              "name": "Can I take physical delivery?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes, delivery and redemption options are available for KDRGOLD and KDRSILVER tokens. Contact our institutional team for details on fees, minimums, and delivery timelines."
              }
            },
            {
              "@type": "Question",
              "name": "What is the security model for digital gold?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "All physical metals are stored at Kitco DR Vault using government depositories, fully bonded and insured. Digital custody uses Fireblocks institutional-grade security with ERC-3643 compliance for on-chain transparency."
              }
            },
            {
              "@type": "Question",
              "name": "Can I enable trading directly through my platform?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes. Enable trading directly through your platform with a seamless, scalable Precious-Metals-as-a-Service solution. Our APIs and integration tools allow you to offer gold and silver trading, custody, and settlement to your customers under your own brand."
              }
            },
            {
              "@type": "Question",
              "name": "What APIs and integrations are available?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "We offer gold trading APIs, tokenization APIs (ERC-3643), and real-time pricing feeds. Our institutional platform connects seamlessly with trading platforms, custodians, and marketplace partners."
              }
            },
            {
              "@type": "Question",
              "name": "Do you only support gold and silver?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Today we offer digital gold (KDRGOLD) and silver (KDRSILVER). Support for platinum and palladium is planned, subject to approvals and availability."
              }
            },
            {
              "@type": "Question",
              "name": "Will more metals be added?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "We're exploring additional precious metals—beginning with tokenized platinum and palladium—pending operational readiness."
              }
            },
            {
              "@type": "Question",
              "name": "What are the storage fees for gold and silver?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Kitco DirectReserve offers low-cost trading with competitive storage fees for gold and silver. Contact our institutional sales team for detailed pricing based on your trading volume and custody requirements."
              }
            }
          ]
        })}
      </script>

      {/* CTA Section */}
      <section className="py-20 bg-kitco-gold" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-kitco-navy mb-6" data-testid="cta-title">
            {t('home.cta.title')}
          </h2>
          <p className="text-xl text-grey-600 mb-8" data-testid="cta-subtitle">
            {t('home.cta.description')}
          </p>
          <Link to={`${langPrefix}/contact`}>
            <Button size="lg" variant="outline" className="bg-kitco-navy text-white hover:bg-grey-700 hover:text-white border-kitco-navy font-semibold text-lg px-8 py-6" data-testid="cta-button">
              {t('home.cta.button')} <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;